/**
 * OrderListFilterBar: the status pills and payment-method chip above a
 * vendor's orders list, driven by the URL (hooks/useOrderListFilters.js).
 *
 * One bar for the shop, pharmacy and brand lists, so a dashboard card lands
 * on the same control on each: the pill for the status it counted is lit,
 * and "Any status" takes it off. A list the pills do not offer as one pill
 * (the brand's pending delivery is three statuses) shows as its own lit pill
 * named after its statuses. A `method` filter shows as a removable chip.
 *
 * Presentational: `delivery` and `method` are the current lists, the
 * callbacks write the URL. `statuses` is [{ value, label }].
 */
import React from "react";
import { DELIVERY_STATE_CFG } from "../../utilities/paymentStatus";
import "./OrderListFilterBar.css";

export const DEFAULT_STATUS_PILLS = ["PROCESSING", "ASSIGNED", "DISPATCHED", "DELIVERED", "REJECTED"].map((value) => ({
  value,
  label: DELIVERY_STATE_CFG[value]?.label || value,
}));

const labelFor = (value) => DELIVERY_STATE_CFG[value]?.label || value;
const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

const OrderListFilterBar = ({
  delivery = [],
  method = [],
  statuses = DEFAULT_STATUS_PILLS,
  onDelivery,
  onMethod,
  summary,
  style,
}) => {
  const offered = statuses.map((s) => [s.value]);
  const customList = delivery.length > 0 && !offered.some((l) => sameList(l, delivery)) ? delivery : null;

  return (
    <div className="lk-orderfilter" role="group" aria-label="Filter orders" style={style}>
      <div className="lk-orderfilter-pills">
        <button
          type="button"
          className={`lk-orderfilter-pill${delivery.length === 0 ? " is-active" : ""}`}
          aria-pressed={delivery.length === 0}
          onClick={() => onDelivery([])}
        >
          Any status
        </button>
        {statuses.map((s) => {
          const active = sameList(delivery, [s.value]);
          return (
            <button
              key={s.value}
              type="button"
              className={`lk-orderfilter-pill${active ? " is-active" : ""}`}
              aria-pressed={active}
              onClick={() => onDelivery(active ? [] : [s.value])}
            >
              {s.label}
            </button>
          );
        })}
        {customList && (
          <button
            type="button"
            className="lk-orderfilter-pill is-active"
            aria-pressed="true"
            title="Set by the dashboard card. Any status clears it."
            onClick={() => onDelivery([])}
          >
            {customList.map(labelFor).join(", ")}
          </button>
        )}
      </div>
      {method.length > 0 && (
        <span className="lk-orderfilter-chip">
          Paid by {method.join(", ")}
          <button type="button" onClick={() => onMethod([])} aria-label="Remove payment method filter" title="Remove">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </span>
      )}
      {summary && <span className="lk-orderfilter-summary">{summary}</span>}
    </div>
  );
};

export default OrderListFilterBar;
